/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useAccessToken,
  useIsLoggedIn,
  useRefreshToken,
} from '@airasia-rn/account/hooks/account';
import { useWebNavigation } from '../contexts';
import { useWebConfig } from '../config/WebConfigProvider';
import { useDeviceInfo } from '@airasia-phoenix/react-native/utils';

const getChatbot = () => {
  if (typeof window === 'undefined') return undefined;
  return window.chatbot;
};

const toLocale = (locale?: string | null) => {
  if (!locale) return 'en-GB';

  const [lang, region] = locale.split('-');
  return `${lang}-${region?.toUpperCase()}`;
};

const retryUntil = (
  fn: () => boolean,
  { maxAttempts = 10, delay = 300 } = {},
) => {
  let attempts = 0;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const tryFn = () => {
    if (fn() || attempts >= maxAttempts) {
      timeoutId && clearTimeout(timeoutId);
      return;
    }
    attempts += 1;
    timeoutId = setTimeout(tryFn, delay);
  };

  tryFn();

  return () => timeoutId && clearTimeout(timeoutId);
};

/**
 * Hook that loads and configures the AskBo chatbot. Use this when you don't need
 * context (e.g. single consumer, direct parent-child).
 */
export const useAskBoChatBot = (
  locale?: string | null,
  { hideDefaultLauncher = false } = {},
) => {
  const configuredRef = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const { data: accessToken } = useAccessToken('searchWebAskBo');
  const { data: refreshToken } = useRefreshToken('searchWebAskBo');
  const { isLoggedIn } = useIsLoggedIn();
  const { openAccountMenu } = useWebNavigation();
  const {
    config: { askBoConfig },
  } = useWebConfig();
  const { scriptId = 'askbo-chatbot-script', scriptUrl } = askBoConfig || {};
  const { isMobileBrowser } = useDeviceInfo();

  const openChatWithBo = useCallback((workflowId?: string) => {
    setShouldLoad(true);

    retryUntil(() => {
      const chatbot = getChatbot();
      if (!chatbot) return false;

      if (
        typeof chatbot.setConfig !== 'function' ||
        typeof chatbot.open !== 'function'
      ) {
        console.error('AskBo: chatbot API unavailable or changed. Halting.');
        return true;
      }

      if (workflowId) {
        chatbot.setConfig({ workflowId });
      }
      chatbot.open();
      return true;
    });
  }, []);

  const configure = useCallback(() => {
    const chatbot = getChatbot();

    if (!chatbot || configuredRef.current) return false;

    chatbot.setConfig?.({
      tooltipLabel: 'AskBo',
      onLoginInitiate: openAccountMenu,
    });

    chatbot.setLocale?.(toLocale(locale));

    if (isLoggedIn && accessToken && refreshToken) {
      chatbot.authorize?.(accessToken, refreshToken);
    } else {
      chatbot.logout?.();
    }

    configuredRef.current = true;
    setIsReady(true);

    return true;
  }, [locale, isLoggedIn, accessToken, refreshToken, openAccountMenu]);

  useEffect(() => {
    if (!shouldLoad) return;

    let cleanupFn: (() => void) | undefined;

    const runConfigure = () => {
      configuredRef.current = false;
      cleanupFn = retryUntil(configure);
    };

    const hideLauncher = () => {
      const launcher = document.getElementById('chatbot-container');
      if (launcher) launcher.style.display = 'none';
    };

    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      (existingScript as HTMLScriptElement).onload = () => {
        runConfigure();
        if (hideDefaultLauncher) setTimeout(hideLauncher, 500);
      };

      runConfigure();
      if (hideDefaultLauncher) hideLauncher();
      return () => cleanupFn?.();
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = scriptUrl || '';
    script.async = true;
    script.onload = () => {
      runConfigure();
      if (hideDefaultLauncher) setTimeout(hideLauncher, 500);
    };
    script.onerror = () => {
      console.error('Failed to load chatbot script:', scriptUrl);
      cleanupFn?.();
    };

    document.body.appendChild(script);

    return () => cleanupFn?.();
  }, [configure, shouldLoad]);

  // Reset configuration when auth state changes
  useEffect(() => {
    configuredRef.current = false;
  }, [accessToken, refreshToken, isLoggedIn]);

  const preload = useCallback(() => {
    setShouldLoad(true);
  }, []);

  // if it's mobile we preload after 2 seconds for LCP , we don't wait for hover
  useEffect(() => {
    if (isMobileBrowser)
      setTimeout(() => {
        preload();
      }, 10000);
  });

  return { openChatWithBo, preload, isReady: true };
};
